/**
 * Authentication & Authorization System
 * Unified auth system for the CRM — Backend-based authentication
 * No AWS dependencies. Login is handled by the Backend API.
 */

import { backendClient } from "./backend-client"

// ─── Types ────────────────────────────────────────────────────────────────────
export type UserRole = "owner" | "admin" | "manager" | "agent" | "doctor" | "receptionist" | "nurse" | "viewer"

export interface AuthUser {
  id: string
  tenantId: string
  email: string
  name: string
  role: UserRole
  avatar?: string
  permissions?: string[]
}

export interface AuthTenant {
  id: string
  name: string
  country: string
  currency: string
  language: string
  timezone?: string
  branding?: {
    name: string
    primaryColor: string
    logo?: string
  }
  settings?: {
    features: {
      aiChatbot: boolean
      leadScoring: boolean
      omnichannel: boolean
    }
  }
}

export interface LoginResult {
  user: AuthUser
  tenant: AuthTenant
  token: string
}

// ─── Storage keys ─────────────────────────────────────────────────────────────
const AUTH_USER_KEY = "crm_auth_user"
const AUTH_TENANT_KEY = "crm_auth_tenant"
const AUTH_TOKEN_KEY = "crm_auth_token"

// ─── Permissions cache (loaded from API) ──────────────────────────────────────

const fallbackRolePermissions: RolePermissions = {
  owner: {
    leads: { view: true, create: true, edit: true, delete: true, assign: true, closeDeal: true },
    properties: { view: true, create: true, edit: true, delete: true },
    quotes: { view: true, create: true, edit: true, delete: true, send: true },
    reports: { view: true, export: true },
    settings: { view: true, edit: true, manageUsers: true, manageRoles: true },
    pipeline: { view: true, edit: true, moveLeads: true, forceClose: true },
  },
  admin: {
    leads: { view: true, create: true, edit: true, delete: true, assign: true, closeDeal: true },
    properties: { view: true, create: true, edit: true, delete: true },
    quotes: { view: true, create: true, edit: true, delete: true, send: true },
    reports: { view: true, export: true },
    settings: { view: true, edit: true, manageUsers: true, manageRoles: false },
    pipeline: { view: true, edit: true, moveLeads: true, forceClose: true },
  },
  manager: {
    leads: { view: true, create: true, edit: true, delete: false, assign: true, closeDeal: true },
    properties: { view: true, create: true, edit: true, delete: false },
    quotes: { view: true, create: true, edit: true, delete: false, send: true },
    reports: { view: true, export: true },
    settings: { view: true, edit: false, manageUsers: false, manageRoles: false },
    pipeline: { view: true, edit: false, moveLeads: true, forceClose: false },
  },
  agent: {
    leads: { view: true, create: true, edit: true, delete: false, assign: false, closeDeal: true },
    properties: { view: true, create: false, edit: false, delete: false },
    quotes: { view: true, create: true, edit: true, delete: false, send: true },
    reports: { view: true, export: false },
    settings: { view: false, edit: false, manageUsers: false, manageRoles: false },
    pipeline: { view: true, edit: false, moveLeads: true, forceClose: false },
  },
  doctor: {
    leads: { view: true, create: true, edit: true, delete: false, assign: false, closeDeal: true },
    properties: { view: true, create: false, edit: false, delete: false },
    quotes: { view: true, create: true, edit: true, delete: false, send: true },
    reports: { view: true, export: true },
    settings: { view: false, edit: false, manageUsers: false, manageRoles: false },
    pipeline: { view: true, edit: false, moveLeads: true, forceClose: false },
  },
  receptionist: {
    leads: { view: true, create: true, edit: true, delete: false, assign: true, closeDeal: false },
    properties: { view: true, create: false, edit: false, delete: false },
    quotes: { view: true, create: true, edit: false, delete: false, send: false },
    reports: { view: false, export: false },
    settings: { view: false, edit: false, manageUsers: false, manageRoles: false },
    pipeline: { view: true, edit: false, moveLeads: true, forceClose: false },
  },
  nurse: {
    leads: { view: true, create: false, edit: true, delete: false, assign: false, closeDeal: false },
    properties: { view: true, create: false, edit: false, delete: false },
    quotes: { view: true, create: false, edit: false, delete: false, send: false },
    reports: { view: true, export: false },
    settings: { view: false, edit: false, manageUsers: false, manageRoles: false },
    pipeline: { view: true, edit: false, moveLeads: false, forceClose: false },
  },
  viewer: {
    leads: { view: true, create: false, edit: false, delete: false, assign: false, closeDeal: false },
    properties: { view: true, create: false, edit: false, delete: false },
    quotes: { view: true, create: false, edit: false, delete: false, send: false },
    reports: { view: true, export: false },
    settings: { view: false, edit: false, manageUsers: false, manageRoles: false },
    pipeline: { view: true, edit: false, moveLeads: false, forceClose: false },
  },
}

export type RolePermissions = Record<string, Record<string, Record<string, boolean>>>

let cachedPermissions: RolePermissions | null = null
let permissionsLoaded = false
let permissionsLoadPromise: Promise<RolePermissions> | null = null

export async function loadPermissionsFromAPI(): Promise<RolePermissions> {
  if (typeof window === "undefined") return fallbackRolePermissions
  if (permissionsLoaded && cachedPermissions) return cachedPermissions
  if (permissionsLoadPromise) return permissionsLoadPromise

  permissionsLoadPromise = (async () => {
    try {
      const res = await fetch("/api/permissions")
      if (res.ok) {
        const data = await res.json()
        cachedPermissions = data
        permissionsLoaded = true
        return data
      }
    } catch { /* fallback silently */ }
    permissionsLoaded = true
    return fallbackRolePermissions
  })()

  return permissionsLoadPromise
}

function getCurrentPermissions(): RolePermissions {
  return cachedPermissions || fallbackRolePermissions
}

if (typeof window !== "undefined") {
  loadPermissionsFromAPI()
}

export function refreshPermissionsCache(newPermissions: RolePermissions) {
  cachedPermissions = newPermissions
  permissionsLoaded = true
}

// ─── Auth functions (Backend-based) ───────────────────────────────────────────

const BACKEND_URL = process.env.NEXT_PUBLIC_API_URL || process.env.API_URL || "http://localhost:3000"

/**
 * Authenticate against the Backend API.
 * The Backend validates credentials and returns user + tenant + JWT token.
 */
export async function loginWithBackend(email: string, password: string): Promise<LoginResult> {
  const res = await fetch(`${BACKEND_URL}/api/v1/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.error || "Invalid credentials")
  }

  const data = await res.json()
  if (!data.success || !data.data) {
    throw new Error("Login failed")
  }

  const { user, tenant, token } = data.data

  // Persist in localStorage
  if (typeof window !== "undefined") {
    localStorage.setItem(AUTH_TOKEN_KEY, token)
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user))
    if (tenant) localStorage.setItem(AUTH_TENANT_KEY, JSON.stringify(tenant))
  }

  return { user, tenant, token }
}

/**
 * Fetch the tenants associated with the authenticated user from the Backend.
 */
export async function getUserTenants(token: string): Promise<AuthTenant[]> {
  const response = await backendClient.request<AuthTenant[]>("/tenants", { token })
  if (response.error) throw new Error(response.error)
  return response.data || []
}

// ─── Local storage helpers ────────────────────────────────────────────────────

export function selectTenant(tenant: AuthTenant): void {
  if (typeof window !== "undefined") {
    localStorage.setItem(AUTH_TENANT_KEY, JSON.stringify(tenant))
    const user = getUser()
    if (user) {
      user.tenantId = tenant.id
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user))
    }
  }
}

export function logout(): void {
  if (typeof window !== "undefined") {
    localStorage.removeItem(AUTH_USER_KEY)
    localStorage.removeItem(AUTH_TENANT_KEY)
    localStorage.removeItem(AUTH_TOKEN_KEY)
  }
}

export function getUser(): AuthUser | null {
  if (typeof window === "undefined") return null
  try {
    const data = localStorage.getItem(AUTH_USER_KEY)
    return data ? JSON.parse(data) : null
  } catch { return null }
}

export function setUser(user: AuthUser): void {
  if (typeof window !== "undefined") {
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user))
  }
}

export function getTenant(): AuthTenant | null {
  if (typeof window === "undefined") return null
  try {
    const data = localStorage.getItem(AUTH_TENANT_KEY)
    return data ? JSON.parse(data) : null
  } catch { return null }
}

export function getToken(): string | null {
  if (typeof window === "undefined") return null
  return localStorage.getItem(AUTH_TOKEN_KEY)
}

export function isAuthenticated(): boolean {
  return getUser() !== null && getTenant() !== null
}

// ─── Permission checking ──────────────────────────────────────────────────────
export type PermissionModule = "leads" | "properties" | "quotes" | "reports" | "settings" | "pipeline"
export type PermissionAction = "view" | "create" | "edit" | "delete" | "assign" | "closeDeal" | "send" | "export" | "manageUsers" | "manageRoles" | "moveLeads" | "forceClose"

export function hasPermission(role: UserRole, module: PermissionModule, action: PermissionAction): boolean {
  const allPermissions = getCurrentPermissions()
  // For unknown/custom roles, fall back to agent-level permissions
  const permissions = allPermissions[role] || allPermissions["agent"] || {}
  if (!permissions) return false
  const modulePerms = permissions[module]
  if (!modulePerms) return false
  return (modulePerms as Record<string, boolean>)[action] === true
}

export function canView(role: UserRole, module: PermissionModule): boolean { return hasPermission(role, module, "view") }
export function canCreate(role: UserRole, module: PermissionModule): boolean { return hasPermission(role, module, "create") }
export function canEdit(role: UserRole, module: PermissionModule): boolean { return hasPermission(role, module, "edit") }
export function canDelete(role: UserRole, module: PermissionModule): boolean { return hasPermission(role, module, "delete") }

export function getRolePermissions(role: UserRole) {
  const allPermissions = getCurrentPermissions()
  return allPermissions[role] || allPermissions.viewer
}

// ─── Role hierarchy ───────────────────────────────────────────────────────────
const ROLE_HIERARCHY: Record<string, number> = { owner: 5, admin: 4, manager: 3, doctor: 3, agent: 2, receptionist: 2, nurse: 2, viewer: 1 }

export function isRoleHigherOrEqual(userRole: UserRole, requiredRole: UserRole): boolean {
  return (ROLE_HIERARCHY[userRole] || 2) >= (ROLE_HIERARCHY[requiredRole] || 2)
}

// ─── UI labels ────────────────────────────────────────────────────────────────
export const ROLE_LABELS = {
  en: { owner: "Owner", admin: "Administrator", manager: "Manager", agent: "Agent", doctor: "Doctor", receptionist: "Receptionist", nurse: "Nurse", viewer: "Viewer" },
  es: { owner: "Propietario", admin: "Administrador", manager: "Gerente", agent: "Agente", doctor: "Doctor", receptionist: "Recepcionista", nurse: "Enfermero/a", viewer: "Visualizador" },
}

export const PERMISSION_LABELS = {
  en: { view: "View", create: "Create", edit: "Edit", delete: "Delete", assign: "Assign", closeDeal: "Close Deal", send: "Send", export: "Export", manageUsers: "Manage Users", manageRoles: "Manage Roles", moveLeads: "Move Leads", forceClose: "Force Close" },
  es: { view: "Ver", create: "Crear", edit: "Editar", delete: "Eliminar", assign: "Asignar", closeDeal: "Cerrar Trato", send: "Enviar", export: "Exportar", manageUsers: "Gestionar Usuarios", manageRoles: "Gestionar Roles", moveLeads: "Mover Leads", forceClose: "Forzar Cierre" },
}

export const MODULE_LABELS = {
  en: { leads: "Leads", properties: "Properties", quotes: "Quotes", reports: "Reports", settings: "Settings", pipeline: "Pipeline" },
  es: { leads: "Leads", properties: "Propiedades", quotes: "Cotizaciones", reports: "Reportes", settings: "Configuración", pipeline: "Pipeline" },
}
